<script setup lang="ts">
import { computed, onMounted, ref } from "vue";

import { api, type GalleryDocument, type GalleryItemDocument } from "./api";

const props = defineProps<{ siteId: string }>();
const document = ref<GalleryDocument | null>(null);
const selectedIndex = ref(0);
const canEdit = ref(false);
const loading = ref(true);
const saving = ref(false);
const uploading = ref(false);
const error = ref("");
const notice = ref("");
const draggedIndex = ref<number | null>(null);
const item = computed(() => document.value?.items[selectedIndex.value] ?? null);

function blankItem(): GalleryItemDocument {
  return {
    kind: "image",
    mediaUrl: "",
    title: "",
    caption: "",
    altText: "",
    isFeatured: false,
    isPublished: true,
  };
}

function addByUrl() {
  if (!document.value) return;
  document.value.items.push(blankItem());
  selectedIndex.value = document.value.items.length - 1;
}

function removeItem() {
  if (!document.value || !item.value) return;
  document.value.items.splice(selectedIndex.value, 1);
  selectedIndex.value = Math.max(0, selectedIndex.value - 1);
}

function moveItem(from: number, to: number) {
  if (!document.value || from === to || to < 0 || to >= document.value.items.length) return;
  const [moved] = document.value.items.splice(from, 1);
  if (!moved) return;
  document.value.items.splice(to, 0, moved);
  selectedIndex.value = to;
}

function dropItem(index: number) {
  if (draggedIndex.value !== null) moveItem(draggedIndex.value, index);
  draggedIndex.value = null;
}

async function uploadMedia(event: Event) {
  const input = event.target as HTMLInputElement;
  const files = [...(input.files ?? [])];
  if (!files.length || !document.value || !canEdit.value) return;
  uploading.value = true;
  error.value = "";
  notice.value = "";
  try {
    for (const file of files) {
      const { asset } = await api.uploadGalleryAsset(props.siteId, file);
      document.value.items.push({
        ...blankItem(),
        kind: asset.kind,
        mediaUrl: asset.url,
        title: file.name.replace(/\.[^.]+$/, "").replace(/[-_]+/g, " "),
        altText: asset.kind === "image" ? "A cat in Jackie's care" : "",
      });
    }
    selectedIndex.value = document.value.items.length - 1;
    notice.value = `${files.length} ${files.length === 1 ? "item" : "items"} uploaded. Add captions, then save the gallery.`;
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : "Unable to upload gallery media";
  } finally {
    uploading.value = false;
    input.value = "";
  }
}

async function save() {
  if (!document.value || !canEdit.value) return;
  saving.value = true;
  error.value = "";
  notice.value = "";
  try {
    document.value = (await api.updateGallery(props.siteId, document.value)).gallery;
    selectedIndex.value = Math.min(selectedIndex.value, Math.max(0, document.value.items.length - 1));
    notice.value = "Gallery saved.";
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : "Unable to save the gallery";
  } finally {
    saving.value = false;
  }
}

onMounted(async () => {
  try {
    const response = await api.gallery(props.siteId);
    document.value = response.gallery;
    canEdit.value = response.canEdit;
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : "Unable to load the gallery";
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <section class="gallery-card">
    <div class="module-heading">
      <div>
        <p class="eyebrow">Photos &amp; short videos</p>
        <h2>Gallery</h2>
        <p>Upload reassuring moments from visits and choose exactly what appears publicly.</p>
      </div>
      <button type="button" :disabled="saving || !canEdit" @click="save">
        {{ saving ? "Saving…" : "Save gallery" }}
      </button>
    </div>

    <p v-if="error" class="message error" role="alert">{{ error }}</p>
    <p v-if="notice" class="message success" role="status">{{ notice }}</p>
    <div v-if="loading" class="loading">Loading gallery…</div>

    <template v-else-if="document">
      <div class="gallery-fields">
        <label>Page heading<input v-model="document.title" :disabled="!canEdit" maxlength="160"></label>
        <label class="wide">Introduction<textarea v-model="document.introduction" :disabled="!canEdit" rows="3" maxlength="1000" /></label>
      </div>

      <div class="upload-row">
        <label class="upload-button">
          {{ uploading ? "Uploading…" : "Upload photos or videos" }}
          <input :disabled="uploading || !canEdit" type="file" multiple accept="image/png,image/jpeg,image/webp,video/mp4,video/webm" @change="uploadMedia">
        </label>
        <button type="button" :disabled="!canEdit" @click="addByUrl">Add by URL</button>
        <span>Images up to 15 MB; MP4/WebM videos up to 75 MB.</span>
      </div>

      <div v-if="document.items.length" class="gallery-workspace">
        <nav class="media-list" aria-label="Gallery items">
          <button
            v-for="(media, index) in document.items"
            :key="media.id || `${media.mediaUrl}-${index}`"
            type="button"
            draggable="true"
            :class="{ active: selectedIndex === index, hidden: !media.isPublished }"
            @click="selectedIndex = index"
            @dragstart="draggedIndex = index"
            @dragover.prevent
            @drop="dropItem(index)"
          >
            <span class="thumb">
              <img v-if="media.kind === 'image' && media.mediaUrl" :src="media.mediaUrl" alt="">
              <span v-else aria-hidden="true">▶</span>
            </span>
            <span><strong>{{ media.title || `Item ${index + 1}` }}</strong><small>{{ media.kind }} · {{ media.isPublished ? "Published" : "Hidden" }}</small></span>
            <span class="drag" aria-hidden="true">⋮⋮</span>
          </button>
        </nav>

        <article v-if="item" class="media-editor">
          <div class="media-preview">
            <img v-if="item.kind === 'image' && item.mediaUrl" :src="item.mediaUrl" :alt="item.altText">
            <video v-else-if="item.kind === 'video' && item.mediaUrl" :src="item.mediaUrl" controls preload="metadata" />
            <span v-else>Paste a media URL below.</span>
          </div>
          <div class="item-toolbar">
            <strong>{{ item.title || "Untitled item" }}</strong>
            <div>
              <button type="button" :disabled="!canEdit || selectedIndex === 0" @click="moveItem(selectedIndex, selectedIndex - 1)">Earlier</button>
              <button type="button" :disabled="!canEdit || selectedIndex === document.items.length - 1" @click="moveItem(selectedIndex, selectedIndex + 1)">Later</button>
              <button class="remove" type="button" :disabled="!canEdit" @click="removeItem">Remove</button>
            </div>
          </div>
          <div class="item-fields">
            <label>Type<select v-model="item.kind" :disabled="!canEdit"><option value="image">Photo</option><option value="video">Video</option></select></label>
            <label>Title or cat name<input v-model="item.title" :disabled="!canEdit" maxlength="160"></label>
            <label class="wide">Media URL<input v-model="item.mediaUrl" :disabled="!canEdit" maxlength="500"></label>
            <label class="wide">Caption<textarea v-model="item.caption" :disabled="!canEdit" rows="3" maxlength="500" /></label>
            <label class="wide">Image description<input v-model="item.altText" :disabled="!canEdit" maxlength="300" placeholder="Describe the photo for visitors who cannot see it"></label>
            <div class="checks"><label><input v-model="item.isPublished" :disabled="!canEdit" type="checkbox"> Published</label><label><input v-model="item.isFeatured" :disabled="!canEdit" type="checkbox"> Featured</label></div>
          </div>
        </article>
      </div>
      <div v-else class="empty"><strong>No gallery items yet.</strong><span>Upload Jackie’s first cat photo or video above.</span></div>
    </template>
  </section>
</template>

<style scoped>
.gallery-card{display:grid;gap:1.2rem;padding:1.5rem;border:1px solid #d9ddd8;border-radius:1rem;background:#fff}.module-heading,.item-toolbar{display:flex;align-items:center;justify-content:space-between;gap:1rem}.module-heading h2{margin:0}.module-heading p:last-child{margin:.35rem 0 0;color:#6d7972}.eyebrow{margin:0 0 .25rem;color:#6b7b72;font-size:.68rem;font-weight:800;letter-spacing:.1em;text-transform:uppercase}.gallery-fields,.item-fields{display:grid;grid-template-columns:1fr 1fr;gap:.85rem}.wide{grid-column:1/-1}label{display:grid;gap:.4rem}textarea{width:100%;padding:.75rem;resize:vertical;border:1px solid #cbd5ce;border-radius:.65rem;background:#fff;font:inherit}.upload-row{display:flex;align-items:center;gap:.7rem;flex-wrap:wrap;padding:1rem;border:1px dashed #aebdb4;border-radius:.8rem;background:#f7faf7}.upload-row>span{color:#6d7972;font-size:.75rem}.upload-button{display:block;padding:.7rem 1rem;border-radius:.65rem;color:#fff;background:#285c47;cursor:pointer}.upload-button input{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0)}.gallery-workspace{display:grid;grid-template-columns:minmax(15rem,.58fr) minmax(0,1.42fr);gap:1rem}.media-list{display:grid;max-height:48rem;align-content:start;overflow-y:auto;gap:.45rem}.media-list>button{display:grid;padding:.45rem;grid-template-columns:3.5rem 1fr auto;gap:.7rem;align-items:center;color:#365045;background:#f4f7f4;text-align:left}.media-list>button.active{color:#fff;background:#285c47}.media-list>button.hidden{opacity:.62}.media-list strong,.media-list small{display:block}.media-list small{margin-top:.2rem;font-size:.65rem;text-transform:capitalize}.thumb{display:grid;width:3.5rem;aspect-ratio:1;overflow:hidden;place-items:center;border-radius:.45rem;background:#dce5df}.thumb img{width:100%;height:100%;object-fit:cover}.drag{opacity:.55;letter-spacing:-.2em}.media-editor{display:grid;align-content:start;gap:1rem;padding:1rem;border:1px solid #dce2dc;border-radius:.8rem;background:#fafbf9}.media-preview{display:grid;min-height:21rem;overflow:hidden;place-items:center;border-radius:.65rem;color:#6d7972;background:#e7ece8}.media-preview img,.media-preview video{width:100%;height:100%;max-height:32rem;object-fit:contain}.item-toolbar>div,.checks{display:flex;gap:.5rem}.item-toolbar button{padding:.5rem .65rem;color:#365045;background:#e8eeea}.remove{color:#91382f!important;background:#fae9e7!important}.checks{align-items:end}.checks label{display:flex;align-items:center}.checks input{width:auto}.message{margin:0;padding:.85rem 1rem;border-radius:.65rem}.message.error{color:#86251f;background:#fee9e7}.message.success{color:#295b40;background:#e2f1e7}.loading,.empty{display:grid;padding:2rem;gap:.35rem;color:#6d7972;text-align:center}@media(max-width:900px){.gallery-workspace{grid-template-columns:1fr}.media-list{display:flex;overflow-x:auto}.media-list>button{min-width:15rem}}@media(max-width:650px){.module-heading,.item-toolbar{align-items:stretch;flex-direction:column}.gallery-fields,.item-fields{grid-template-columns:1fr}.wide{grid-column:auto}.item-toolbar>div,.checks{flex-wrap:wrap}}
</style>
